<script setup lang="ts">
//#region import
import { computed, ref } from 'vue';
import { isEmpty, pick } from 'lodash-es';
import { useTypedStore } from '@/store';
import { useApi } from '@/api';
import { typeGuard, type EmitsToListeners, type ErrorResponse, type ToErrorMap } from '@/types/core';
import type {
  GetControlTowerCustomerDomainsResponse,
  PutControlTowerCustomerDomainsByCountryCodeData,
} from '@/types/openapi-ts';
//#endregion

//#region types
type PutControlTowerCustomerDomainsByCountryCodeErrors 
  = ToErrorMap<PutControlTowerCustomerDomainsByCountryCodeData['body']>;

interface Props {
  popupData?: GetControlTowerCustomerDomainsResponse['payload'][number] | null,
  selectedCountries?: string[],
}

interface  Emits {
  'close': [],
  'success': [],
};

export interface AddSystemDomainParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  popupData: null,
  selectedCountries: () => [],
});

const emit = defineEmits<Emits>();
//#endregion

//#region composables
const api = useApi();
const store = useTypedStore();
//#endregion

//#region state
const isVisible = ref(false);
const isLoading = ref(false);
const errors = ref<PutControlTowerCustomerDomainsByCountryCodeErrors>({});

const data = ref<{
  country_code: PutControlTowerCustomerDomainsByCountryCodeData['path']['countryCode'],
} & PutControlTowerCustomerDomainsByCountryCodeData['body']>({
  country_code: '',
  front_domain: '',
  panel_domain: '',
  admin_domain: '',
});
//#endregion

//#region computed
const countries = computed(() => store.getters['misc/countries']);
const countriesIndex = computed(() => store.getters['misc/countriesIndex']);

const isEdit = computed(() => {
  return !isEmpty(props.popupData);
});
const filteredCountries = computed(() => {
  return countries.value.filter(({ code }) => !props.selectedCountries.includes(code));
});
//#endregion

//#region methods
const open = () => {
  if(props.popupData) {
    data.value = {
      ...pick(props.popupData, [
        'country_code',
        'front_domain',
        'panel_domain',
        'admin_domain',
      ]),
    };
  } else {
    data.value.country_code = filteredCountries.value[0]?.code ?? '';
  }
  isVisible.value = true;
};

const handleClose = () => {
  emit('close');
  isVisible.value = false;
  errors.value = {};
};

const handleSave = () => {
  api.putCustomerDomains(data.value).then(() => {
    emit('success');
    handleClose();
  }).catch((e) => {
    if(typeGuard<ErrorResponse<PutControlTowerCustomerDomainsByCountryCodeErrors>>(e)){
      errors.value = e?.data?.errors || {};
    }
  });
};
//#endregion

defineExpose({
  open,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="add-payment-method-popup"
    :title="isEdit
      ? `${$t('settings.systemDomains.confirm.editSystemDomains')} - ${countriesIndex[data.country_code]?.name}`
      : $t('settings.systemDomains.confirm.addSystemDomains')"
    :width="480"
    :is-loading="isLoading"
    @close="handleClose"
  >
    <template v-slot:body>
      <div class="t:space-y-16">
        <UiSelectElement
          v-if="!isEdit"
          v-model="data.country_code"
          :placeholder="$t('settings.systemDomains.confirm.notSelected')"
          :label="$t('settings.systemDomains.confirm.country_code')"
          :list="filteredCountries"
          option-value-param="code"
          filterable
          size="big"
        />
        <UiInput
          v-model="data.front_domain"
          :label="$t('settings.systemDomains.confirm.front_domain')"
          autosize
          size="big"
          :placeholder="$t('settings.systemDomains.confirm.front_domain_placeholder')"
          type="text"
          :error="errors.front_domain"
        />
        <UiInput
          v-model="data.panel_domain"
          :label="$t('settings.systemDomains.confirm.panel_domain')"
          autosize
          size="big"
          :placeholder="$t('settings.systemDomains.confirm.panel_domain_placeholder')"
          type="text"
          :error="errors.panel_domain"
        />
        <UiInput
          v-model="data.admin_domain"
          :label="$t('settings.systemDomains.confirm.admin_domain')"
          autosize
          size="big"
          :placeholder="$t('settings.systemDomains.confirm.admin_domain_placeholder')"
          type="text"
          :error="errors.admin_domain"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:justify-end t:flex t:space-x-10">
        <UiButton
          type="negative"
          size="big"
          icon="times"
          @click="handleClose"
        >
          {{ $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          filled
          size="big"
          icon="check"
          @click="handleSave"
        >
          {{ $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">

</style>







